import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Search } from "@digital-twins/design-system/icons";
import type {
  CreativeBrand,
  CreativeProduct,
} from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useCreativeLibrary } from "@/hooks/useCreativeLibrary";
import { cn } from "@/lib/utils";
import type {
  CreativeWorkspaceProps,
  ProductCreationSelection,
  CreativeBrandEditorState,
} from "@/types/creative-library";
import { useStartCreativeThread } from "./useStartCreativeThread";
import { CreativeBrandCards } from "./CreativeBrandCards";
import { BrandEditor } from "./BrandEditor";
import { ProductManager } from "./ProductManager";
import { CreativeLibraryEmptyState } from "./CreativeLibraryEmptyState";

export function BrandLibraryTab({ className }: CreativeWorkspaceProps) {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const library = useCreativeLibrary();
  const [query, setQuery] = useState("");
  const [editor, setEditor] = useState<CreativeBrandEditorState>({
    open: false,
    brand: null,
  });
  const [productBrand, setProductBrand] = useState<CreativeBrand | null>(null);
  const { start, starting, actionError, clearActionError } = useStartCreativeThread();
  const brands = library.brands.filter((brand) =>
    brand.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );
  function useProduct(
    product: CreativeProduct,
    selection?: ProductCreationSelection,
  ) {
    void start(product.token, {
      brandProfileToken: product.brandToken,
      productToken: product.token,
      ...selection,
    });
  }
  return (
    <section
      className={cn("flex min-w-0 flex-col gap-6", className)}
      data-testid="creative-library-brands"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Heading level={2} size="xl">
            {t("creativeLibrary.brands")}
          </Heading>
          <Text size="sm" color="muted">
            {t("creativeLibrary.brandsHint")}
          </Text>
        </div>
        <Button
          variant="primary"
          className="gap-2"
          onClick={() => setEditor({ open: true, brand: null })}
          data-testid="creative-new-brands"
        >
          <Plus className="h-4 w-4" />
          {t("creativeLibrary.newBrand")}
        </Button>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("creativeLibrary.search")}
          aria-label={t("creativeLibrary.search")}
          className="pl-9"
          data-testid="creative-library-search"
        />
      </div>
      {(library.error || actionError) && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl border border-destructive/30 p-4"
          role="alert"
        >
          <Text size="sm" className="text-destructive">
            {t(
              actionError
                ? "creativeLibrary.startFailed"
                : "creativeLibrary.loadFailed",
            )}
          </Text>
          <Button
            variant="outline"
            onClick={() => {
              clearActionError();
              void library.reload();
            }}
          >
            {t("creativeLibrary.retry")}
          </Button>
        </div>
      )}
      {library.loading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((n) => (
            <Skeleton key={n} className="h-64 rounded-2xl" />
          ))}
        </div>
      ) : (
        (!library.error || library.hasData) &&
        (brands.length ? (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <CreativeBrandCards
              brands={brands}
              allBrands={library.brands}
              creatives={library.creatives}
              onEdit={(brand) => setEditor({ open: true, brand })}
              onProducts={setProductBrand}
              onSubjects={(brand) =>
                navigate(
                  `/knowledge?view=creatives&brand=${brand.token}&models=1`,
                )
              }
              onCreate={(brand) =>
                void start(brand.token, { brandProfileToken: brand.token })
              }
              startingToken={starting}
              onChanged={() => void library.reload()}
            />
          </div>
        ) : (
          <CreativeLibraryEmptyState
            kind="brands"
            title={t(
              query
                ? "creativeLibrary.noMatches"
                : "creativeLibrary.empty.brands",
            )}
            description={t("creativeLibrary.brandsHint")}
            actionLabel={!query ? t("creativeLibrary.newBrand") : undefined}
            onCreate={() => setEditor({ open: true, brand: null })}
          />
        ))
      )}
      <BrandEditor
        open={editor.open}
        brand={editor.brand}
        brands={library.brands}
        creatives={library.creatives}
        onClose={() => setEditor({ open: false, brand: null })}
        onSaved={() => void library.reload()}
      />
      <ProductManager
        brand={productBrand}
        products={library.products}
        creatives={library.creatives}
        boards={library.boards}
        onClose={() => setProductBrand(null)}
        onSaved={() => void library.reload()}
        onUse={useProduct}
      />
    </section>
  );
}
